import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, Network } from "lucide-react";
import { toast } from "sonner";
import { MagneticButton } from "../MagneticButton";
import { postJSON, patchJSON } from "../../lib/api";

const PURPOSES = [
    { id: "lan", label: "LAN" },
    { id: "dmz", label: "DMZ" },
    { id: "guest", label: "Guest" },
    { id: "iot", label: "IoT" },
    { id: "management", label: "Management" },
    { id: "voip", label: "VoIP" },
    { id: "wan", label: "WAN" },
    { id: "other", label: "Other" },
];

/** Parse a CIDR string client-side and return a small summary card.
 *  Pure JS — no Stripe-like surprises. Mirrors the backend's IPv4 validation. */
function parseCidr(cidr) {
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/.exec((cidr || "").trim());
    if (!m) return null;
    const octets = [+m[1], +m[2], +m[3], +m[4]];
    if (octets.some((o) => o < 0 || o > 255)) return null;
    const prefix = +m[5];
    if (prefix < 0 || prefix > 32) return null;

    const ipInt = (octets[0] << 24) | (octets[1] << 16) | (octets[2] << 8) | octets[3];
    const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
    const networkInt = (ipInt & mask) >>> 0;
    const broadcastInt = (networkInt | (~mask >>> 0)) >>> 0;
    const toIp = (n) => [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join(".");
    const network = toIp(networkInt);
    const broadcast = toIp(broadcastInt);
    const total = prefix === 32 ? 1 : 2 ** (32 - prefix);
    const usable = prefix >= 31 ? total : Math.max(0, total - 2);
    const firstHost = prefix >= 31 ? network : toIp(networkInt + 1);
    const lastHost = prefix >= 31 ? broadcast : toIp(broadcastInt - 1);
    const suggestedGw = prefix >= 31 ? network : toIp(networkInt + 1);
    const netmask = toIp(mask);
    return { network, broadcast, netmask, firstHost, lastHost, total, usable, suggestedGw, normalized: `${network}/${prefix}` };
}

export const SubnetForm = ({ client, subnet, onClose, onSaved }) => {
    const isEdit = Boolean(subnet);
    const [form, setForm] = useState({
        name: subnet?.name || "",
        cidr: subnet?.cidr || "",
        vlan_id: subnet?.vlan_id ?? "",
        gateway: subnet?.gateway || "",
        dns_servers: (subnet?.dns_servers || []).join(", "),
        purpose: subnet?.purpose || "lan",
        site: subnet?.site || "",
        notes: subnet?.notes || "",
    });
    const [saving, setSaving] = useState(false);
    const [autoFilledGw, setAutoFilledGw] = useState(false);

    const parsed = useMemo(() => parseCidr(form.cidr), [form.cidr]);

    // Auto-suggest gateway = first usable host of the CIDR (.1 for /24 etc.)
    // Only fills it once, and only if the user hasn't already typed one.
    useEffect(() => {
        if (parsed && !form.gateway && !autoFilledGw) {
            setForm((f) => ({ ...f, gateway: parsed.suggestedGw }));
            setAutoFilledGw(true);
        }
    }, [parsed, form.gateway, autoFilledGw]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        if (!parsed) { toast.error("Invalid CIDR"); return; }

        const payload = {
            name: form.name.trim(),
            cidr: parsed.normalized,
            vlan_id: form.vlan_id === "" ? null : Number(form.vlan_id),
            gateway: form.gateway.trim() || null,
            dns_servers: form.dns_servers.split(",").map((s) => s.trim()).filter(Boolean),
            purpose: form.purpose,
            site: form.site.trim() || null,
            notes: form.notes || null,
        };

        setSaving(true);
        try {
            if (isEdit) {
                await patchJSON(`/ipam/subnets/${subnet.id}`, payload);
                toast.success("Subnet updated");
            } else {
                await postJSON(`/ipam/clients/${client.id}/subnets`, payload);
                toast.success("Subnet created");
            }
            onSaved();
        } catch (err) {
            if (err.status === 409) {
                toast.error(err.payload?.detail?.message || "CIDR overlaps with an existing subnet");
            } else if (err.status === 422) {
                toast.error(
                    typeof err.payload?.detail === "string"
                        ? err.payload.detail
                        : "Validation failed — check fields",
                );
            } else {
                toast.error(err.message || "Save failed");
            }
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-canvas/80 backdrop-blur-md"
            data-testid="subnet-form-modal"
            role="dialog"
            aria-modal="true"
        >
            <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 cursor-default" />
            <form
                onSubmit={submit}
                className="relative w-full max-w-xl rounded-2xl border border-white/[0.08] bg-surface/95 shadow-heavy-popover p-6 md:p-8"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-text-dim hover:text-text-primary p-1.5 rounded"
                    aria-label="Close"
                    data-testid="subnet-form-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                    {isEdit ? "Edit subnet" : "New subnet"}
                </p>
                <h2 className="mt-2 font-display font-semibold text-2xl text-text-primary tracking-tight">
                    {isEdit ? `Edit ${subnet.cidr}` : "Add a subnet"}
                </h2>

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                    <Field label="Name *">
                        <input
                            autoFocus
                            type="text"
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            placeholder="LAN HQ Milano"
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="subnet-field-name"
                        />
                    </Field>
                    <Field label="CIDR *">
                        <input
                            type="text"
                            value={form.cidr}
                            onChange={(e) => update("cidr", e.target.value)}
                            placeholder="10.10.0.0/24"
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] font-mono focus:outline-none focus:border-accent-cyan/40"
                            data-testid="subnet-field-cidr"
                        />
                    </Field>
                    <Field label="VLAN ID (1–4094)">
                        <input
                            type="number"
                            min="1"
                            max="4094"
                            value={form.vlan_id}
                            onChange={(e) => update("vlan_id", e.target.value)}
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="subnet-field-vlan"
                        />
                    </Field>
                    <Field label="Purpose">
                        <select
                            value={form.purpose}
                            onChange={(e) => update("purpose", e.target.value)}
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="subnet-field-purpose"
                        >
                            {PURPOSES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Gateway">
                        <input
                            type="text"
                            value={form.gateway}
                            onChange={(e) => { setAutoFilledGw(true); update("gateway", e.target.value); }}
                            placeholder={parsed ? parsed.suggestedGw : "10.10.0.1"}
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] font-mono focus:outline-none focus:border-accent-cyan/40"
                            data-testid="subnet-field-gateway"
                        />
                    </Field>
                    <Field label="Site">
                        <input
                            type="text"
                            value={form.site}
                            onChange={(e) => update("site", e.target.value)}
                            placeholder="HQ, Branch-Roma, …"
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="subnet-field-site"
                        />
                    </Field>
                    <div className="md:col-span-2">
                        <Field label="DNS servers (comma-separated, max 4)">
                            <input
                                type="text"
                                value={form.dns_servers}
                                onChange={(e) => update("dns_servers", e.target.value)}
                                placeholder="1.1.1.1, 8.8.8.8"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] font-mono focus:outline-none focus:border-accent-cyan/40"
                                data-testid="subnet-field-dns"
                            />
                        </Field>
                    </div>
                </div>

                {parsed ? (
                    <div
                        className="mt-4 rounded-lg border border-accent-cyan/20 bg-accent-cyan/[0.04] p-3 text-[11.5px] font-mono text-text-muted"
                        data-testid="subnet-cidr-preview"
                    >
                        <div className="flex items-center gap-1.5 text-accent-cyan mb-1">
                            <Network className="w-3 h-3" /> Range preview
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                            <span>Network: <span className="text-text-primary">{parsed.network}</span></span>
                            <span>Netmask: <span className="text-text-primary">{parsed.netmask}</span></span>
                            <span>Usable: <span className="text-text-primary">{parsed.firstHost} – {parsed.lastHost}</span></span>
                            <span>Hosts: <span className="text-text-primary">{parsed.usable.toLocaleString()}</span></span>
                            <span>Broadcast: <span className="text-text-primary">{parsed.broadcast}</span></span>
                            <span>Suggested GW: <span className="text-text-primary">{parsed.suggestedGw}</span></span>
                        </div>
                    </div>
                ) : form.cidr ? (
                    <p className="mt-3 text-[11.5px] text-accent-amber" data-testid="subnet-cidr-invalid">
                        Invalid CIDR — use IPv4 format like <span className="font-mono">10.10.0.0/24</span>.
                    </p>
                ) : null}

                <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md border border-white/[0.08] text-[13px] text-text-muted hover:text-text-primary"
                        data-testid="subnet-form-cancel"
                    >
                        Cancel
                    </button>
                    <MagneticButton
                        type="submit"
                        disabled={saving || !parsed || !form.name.trim()}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan disabled:opacity-40"
                        ariaLabel="Save subnet"
                        data-testid="subnet-form-submit"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        {saving ? "Saving…" : (isEdit ? "Save changes" : "Add subnet")}
                    </MagneticButton>
                </div>
            </form>
        </div>
    );
};

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">{label}</span>
        <div className="mt-1">{children}</div>
    </label>
);

export default SubnetForm;
